import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { Button } from 'reactstrap';
import { Link, matchPath, useHistory, useLocation } from "react-router-dom";
import { injectIntl, intlShape } from "react-intl";

import ConfigStore from "../../../../store/ConfigStore";
import TranslationStore from "../../../../store/TranslationStore";
import UIStore from "../../../../store/UIStore";
import MobileNavigationButton from "../../MobileNavigationButton";
import globalTranslations from "../../../../i18n/globalTranslations";
import Hamburger from "../../../common/Hamburger";
import Icon from "../../../common/Icon";
import NavigationEntityPosition from "../../../../types/NavigationEntityPosition";
import { modelOf } from "../../../../prop-types";
import MobileNavigationTab from "../../../../types/MobileNavigationTab";
import Paths from "../../../../types/Paths";
import CustomNavigationLinkStore from "../../../../store/CustomNavigationLinkStore";
import useNavigationEntityConverter from "../../../../hooks/navigation/useNavigationEntityConverter";
import NavigationEntityItemButton from "../../../navigation/NavigationEntityItemButton";
import generatePath from "../../../../util/generatePath";
import RouteService from "../../../../services/RouteService";

/* These navigation components should be reworked smaller etc. */

const MobileIconsNavigation = ({
  configStore,
  customNavigationLinkStore,
  translationStore,
  uiStore,
  intl,
  routeService,
  getNavigationCart,
  onShowSearchOnMobile,
}) => {
  const location = useLocation();
  const history = useHistory();

  const getDefaultMobileMenuTab = () => {
    if (
      matchPath(location.pathname, {
        path: '/:locale?' + Paths.InfoPage,
      })
    ) {
      return MobileNavigationTab.INFO;
    }

    return MobileNavigationTab.PRODUCTS;
  };

  const getNavigationEntityButtonsByPosition = (position) => {
    const navigationLinks =
      customNavigationLinkStore.getNavigationEntitiesByPosition(position);

    if (!navigationLinks.length) {
      return;
    }

    return renderNavigationEntityItemButton(navigationLinks);
  };

  const getMobileIconForAccountIfWebStore = () => {
    return (
      configStore.siteConfig.isWebStore && (
        <MobileNavigationButton
          tab={MobileNavigationTab.ACCOUNT}
          className="ml-auto text-uppercase CommonNavigation__icons"
          ariaLabel={intl.formatMessage(globalTranslations.myAccountTitle)}
        >
          <Icon name="user" className="fa-2x" />
        </MobileNavigationButton>
      )
    );
  };

  const getMobileIconForWishListIfShoppingCentreAndEnabled = () => {
    return (
      configStore.wishlist.enabled &&
      configStore.siteConfig.isShoppingCenter && (
        <MobileNavigationButton
          tab={MobileNavigationTab.ACCOUNT}
          className="ml-auto text-uppercase"
          ariaLabel={intl.formatMessage(globalTranslations.seeCategoriesNavi)}
        >
          <Icon name="star" className="fa-2x" />
        </MobileNavigationButton>
      )
    );
  };

  const renderNavigationEntityItemButton = (navigationLinks) => {
    const navigationEntityItems = useNavigationEntityConverter({
      entities: navigationLinks,
    });

    return navigationEntityItems.map((entity) => (
      <div key={entity.id} className="NavigationEntityButton__button-wrapper">
        <NavigationEntityItemButton
          entity={entity}
          buttonContentClassName={'NavigationEntityButton__button-content'}
        />
      </div>
    ));
  };

  const navigateToStoreInfoPage = () => {
    const storeInfoPagePath = generatePath(Paths.InfoPage, {
      slug: '-',
      id: configStore.storeInfoPageId,
    });
    history.push(routeService.getPath(storeInfoPagePath));
  };

  const mobileLogo = translationStore.mobileLogo;

  return (
    <>
      <MobileNavigationButton
        tab={getDefaultMobileMenuTab()}
        forceClose={uiStore.mobileNavigation.isOpen}
        ariaLabel={intl.formatMessage(globalTranslations.seeCategoriesNavi)}
      >
        <Hamburger active={uiStore.mobileNavigation.isOpen} />
      </MobileNavigationButton>
      <div className="CommonNavigation__mobile-home">
        {getNavigationEntityButtonsByPosition(NavigationEntityPosition.LEFT)}
        <Link to="/">
          <Button
            color="plain"
            className="CommonNavigation__icons"
            aria-label={intl.formatMessage(globalTranslations.homeTitle)}
          >
            {mobileLogo.length > 0 ? (
              <img
                className="CommonNavigation__mobile-logo-img"
                alt={configStore.store.name}
                src={mobileLogo[0].image_path}
                width={mobileLogo[0].width}
                height={mobileLogo[0].height}
              />
            ) : (
              <Icon name="home" className="fa-2x" />
            )}
          </Button>
        </Link>
        {getNavigationEntityButtonsByPosition(NavigationEntityPosition.RIGHT)}
      </div>
      <div className="CommonNavigation__mobile-right">
        {!configStore.siteConfig.isHomePage &&
          !configStore.topNavigation.headerMobileSearchbarVisibility && (
            <Button
              color="plain"
              className="CommonNavigation__icons"
              onClick={onShowSearchOnMobile}
              aria-label={intl.formatMessage(globalTranslations.search)}
            >
              <Icon name="search" className="fa-2x" />
            </Button>
          )}
        {getMobileIconForAccountIfWebStore()}
        {getMobileIconForWishListIfShoppingCentreAndEnabled()}
        {configStore.storeInfoPageId > 0 && (
          <Button
            color="plain"
            className="CommonNavigation__icons"
            onClick={navigateToStoreInfoPage}
            aria-label={intl.formatMessage(globalTranslations.infoTitle)}
          >
            <Icon name="map-marker" className="fa-2x" />
          </Button>
        )}
        {getNavigationCart()}
      </div>
    </>
  );
};

MobileIconsNavigation.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  customNavigationLinkStore: modelOf(CustomNavigationLinkStore).isRequired,
  translationStore: modelOf(TranslationStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  intl: intlShape.isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  getNavigationCart: PropTypes.func.isRequired,
  onShowSearchOnMobile: PropTypes.func.isRequired,
};

export default inject(
  'configStore',
  'customNavigationLinkStore',
  'translationStore',
  'uiStore',
  'routeService',
)(injectIntl(observer(MobileIconsNavigation)));
